var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { txInit, init as core_init, InstantError, } from '@instantdb/core';
import { useCallback, useEffect, useRef, useState, useSyncExternalStore, } from 'react';
import { useQueryInternal } from "./useQuery.js";
import { InstantReactRoom, rooms } from "./InstantReactRoom.js";
const defaultAuthState = {
    isLoading: true,
    user: undefined,
    error: undefined,
};
export default class InstantReactAbstractDatabase {
    constructor(config, versions) {
        this.tx = txInit();
        /**
         * Returns a unique ID for a given `name`. It's stored in local storage,
         * so you will get the same ID across sessions.
         *
         * This is useful for generating IDs that could identify a local device or user.
         *
         * @example
         *  const deviceId = await db.getLocalId('device');
         */
        this.getLocalId = (name) => {
            return this.core.getLocalId(name);
        };
        /**
         * A hook that returns a unique ID for a given `name`. localIds are
         * stored in local storage, so you will get the same ID across sessions.
         *
         * Initially returns `null`, and then loads the localId.
         *
         * @example
         * const deviceId = db.useLocalId('device');
         * if (!deviceId) return null; // loading
         * console.log('Device ID:', deviceId)
         */
        this.useLocalId = (name) => {
            const [localId, setLocalId] = useState(null);
            useEffect(() => {
                let mounted = true;
                const f = () => __awaiter(this, void 0, void 0, function* () {
                    const id = yield this.getLocalId(name);
                    if (!mounted)
                        return;
                    setLocalId(id);
                });
                f();
                return;
            }, [name]);
            return localId;
        };
        /**
         * Hooks for working with rooms
         *
         * @see https://instantdb.com/docs/presence-and-topics
         *
         * @example
         *  const room = db.room('chat', roomId);
         *  const { peers } = db.rooms.usePresence(room);
         *  const publish = db.rooms.usePublishTopic(room, 'emoji');
         *  // ...
         */
        this.rooms = rooms;
        /**
         * Use this to write data! You can create, update, delete, and link objects
         *
         * @see https://instantdb.com/docs/instaml
         *
         * @example
         *   // Create a new object in the `goals` namespace
         *   const goalId = id();
         *   db.transact(db.tx.goals[goalId].update({title: "Get fit"}))
         *
         *   // Update the title
         *   db.transact(db.tx.goals[goalId].update({title: "Get super fit"}))
         *
         *   // Delete it
         *   db.transact(db.tx.goals[goalId].delete())
         *
         *   // Or create an association:
         *   todoId = id();
         *   db.transact([
         *    db.tx.todos[todoId].update({ title: 'Go on a run' }),
         *    db.tx.goals[goalId].link({todos: todoId}),
         *  ])
         */
        this.transact = (chunks) => {
            return this.core.transact(chunks);
        };
        /**
         * Use this to query your data!
         *
         * @see https://instantdb.com/docs/instaql
         *
         * @example
         *   // listen to all goals
         *   const { isLoading, error, data } = db.useQuery({ goals: {} });
         *
         *   // goals where the title is "Get Fit"
         *   const { isLoading, error, data } = db.useQuery({
         *     goals: { $: { where: { title: 'Get Fit' } } },
         *   });
         *
         *   // all goals, _alongside_ their todos
         *   const { isLoading, error, data } = db.useQuery({
         *     goals: { todos: {} },
         *   });
         *
         *   // skip if `user` is not logged in
         *   const { isLoading, error, data } = db.useQuery(
         *     auth.user ? { goals: {} } : null,
         *   );
         */
        this.useQuery = (query, opts) => {
            return useQueryInternal(this.core, query, opts).state;
        };
        /**
         * Listen for the logged in state. This is useful
         * for deciding when to show a login screen.
         *
         * Check out the docs for an example `Login` component too!
         *
         * @see https://instantdb.com/docs/auth
         * @example
         *  function App() {
         *    const { isLoading, user, error } = db.useAuth()
         *    if (isLoading) {
         *      return <div>Loading...</div>
         *    }
         *    if (error) {
         *      return <div>Uh oh! {error.message}</div>
         *    }
         *    if (user) {
         *      return <Main user={user} />
         *    }
         *    return <Login />
         *  }
         *
         */
        this.useAuth = () => {
            // We use a ref to store the result of the query.
            // This is becuase `useSyncExternalStore` uses `Object.is`
            // to compare the previous and next state.
            // If we don't use a ref, the state will always be considered different, so
            // the component will always re-render.
            const resultCacheRef = useRef(this.core._reactor._currentUserCached);
            // Similar to `resultCacheRef`, `useSyncExternalStore` will unsubscribe
            // if `subscribe` changes, so we use `useCallback` to memoize the function.
            const subscribe = useCallback((cb) => {
                const unsubscribe = this.core.subscribeAuth((auth) => {
                    resultCacheRef.current = Object.assign({ isLoading: false }, auth);
                    cb();
                });
                return unsubscribe;
            }, []);
            const state = useSyncExternalStore(subscribe, () => resultCacheRef.current, () => defaultAuthState);
            return state;
        };
        /**
         * Subscribe to the currently logged in user.
         * If the user is not logged in, this hook with throw an Error.
         * You will want to protect any calls of this hook with a
         * <db.SignedIn> component, or your own logic based on db.useAuth()
         *
         * @see https://instantdb.com/docs/auth
         * @throws Error indicating user not signed in
         * @example
         *  function UserDisplay() {
         *    const user = db.useUser()
         *    return <div>Logged in as: {user.email}</div>
         *  }
         *
         *  <db.SignedIn>
         *    <UserDisplay />
         *  </db.SignedIn>
         *
         */
        this.useUser = () => {
            const { user } = this.useAuth();
            if (!user) {
                throw new InstantError('useUser must be used within an auth-protected route');
            }
            return user;
        };
        /**
         * Listen for connection status changes to Instant. Use this for things like
         * showing connection state to users
         *
         * @see https://www.instantdb.com/docs/patterns#connection-status
         * @example
         *  function App() {
         *    const status = db.useConnectionStatus()
         *    const connectionState =
         *      status === 'connecting' || status === 'opened'
         *        ? 'authenticating'
         *      : status === 'authenticated'
         *        ? 'connected'
         *      : status === 'closed'
         *        ? 'closed'
         *      : status === 'errored'
         *        ? 'errored'
         *      : 'unexpected state';
         *
         *    return <div>Connection state: {connectionState}</div>
         *  }
         */
        this.useConnectionStatus = () => {
            const statusRef = useRef(this.core._reactor.status);
            const subscribe = useCallback((cb) => {
                const unsubscribe = this.core.subscribeConnectionStatus((newStatus) => {
                    if (newStatus !== statusRef.current) {
                        statusRef.current = newStatus;
                        cb();
                    }
                });
                return unsubscribe;
            }, []);
            const status = useSyncExternalStore(subscribe, () => statusRef.current, 
            // For SSR, always return 'connecting' as the initial state
            () => 'connecting');
            return status;
        };
        /**
         * Use this for one-off queries.
         * Returns local data if available, otherwise fetches from the server.
         * Because we want to avoid stale data, this method will throw an error
         * if the user is offline or there is no active connection to the server.
         *
         * @see https://instantdb.com/docs/instaql
         *
         * @example
         *
         *  const resp = await db.queryOnce({ goals: {} });
         *  console.log(resp.data.goals)
         */
        this.queryOnce = (query, opts) => {
            return this.core.queryOnce(query, opts);
        };
        /**
         * Only render children if the user is signed in.
         * @see https://instantdb.com/docs/auth
         *
         * @example
         *  <db.SignedIn>
         *    <MyComponent />
         *  </db.SignedIn>
         *
         */
        this.SignedIn = ({ children }) => {
            const auth = this.useAuth();
            if (auth.isLoading || auth.error || !auth.user)
                return null;
            return _jsx(_Fragment, { children: children });
        };
        /**
         * Only render children if the user is signed out.
         * @see https://instantdb.com/docs/auth
         *
         * @example
         *  <db.SignedOut>
         *    <MyComponent />
         *  </db.SignedOut>
         *
         */
        this.SignedOut = ({ children }) => {
            const auth = this.useAuth();
            if (auth.isLoading || auth.error || auth.user)
                return null;
            return _jsx(_Fragment, { children: children });
        };
        this.core = core_init(config, 
        // @ts-expect-error because TS can't resolve subclass statics
        this.constructor.Storage, 
        // @ts-expect-error because TS can't resolve subclass statics
        this.constructor.NetworkListener, versions, 
        // @ts-expect-error because TS can't resolve subclass statics
        this.constructor.EventSourceImpl);
        this._core = this.core;
        this.auth = this.core.auth;
        this.storage = this.core.storage;
    }
    /**
     * Obtain a handle to a room, which allows you to listen to topics and presence data
     *
     * If you don't provide a `type` or `id`, Instant will default to `_defaultRoomType` and `_defaultRoomId`
     * as the room type and id, respectively.
     *
     * @see https://instantdb.com/docs/presence-and-topics
     *
     * @example
     *  const room = db.room('chat', roomId);
     *  const { peers } = db.rooms.usePresence(room);
     */
    room(type = '_defaultRoomType', id = '_defaultRoomId') {
        return new InstantReactRoom(this.core, type, id);
    }
    /**
     * One time query for the logged in state. This is useful
     * for scenarios where you want to know the current auth
     * state without subscribing to changes.
     *
     * @see https://instantdb.com/docs/auth
     * @example
     *   const user = await db.getAuth();
     *   console.log('logged in as', user.email)
     */
    getAuth() {
        return this.core.getAuth();
    }
}
//# sourceMappingURL=InstantReactAbstractDatabase.js.map